/**
 * Permission catalog — the single source of truth for authorization LAYER 1.
 *
 * Mirrors docs/ARCHITECTURE.md §4.1 (42 tenant keys), §4.2 (11 platform keys)
 * and §4.3 (4 reserved keys) = 57 keys.
 *
 * LAYER 1 (`requirePermission`) asks "does this user's role set contain the
 * key?". LAYER 2 (`requireFeature`, see ./features.ts) asks "is the tenant
 * entitled to the capability?". Neither implies the other. `featureKey` records
 * the pairing so the UI can grey out a whole navigation section from a single
 * `/auth/me` response — it is documentation of the pairing, not an implicit
 * check.
 *
 * Declaration order is significant: it is used as `Permission.sortOrder` by the
 * seed and as the display order in the superadmin console.
 */
export const PERMISSIONS = {
    // ── Tenant scope (42) — ARCHITECTURE §4.1 ────────────────────────────────
    'account.read': {
        key: 'account.read',
        label: 'Read account',
        description: 'View the account profile and estate statistics.',
        scope: 'TENANT',
        featureKey: 'account.overview',
    },
    'plans.read': {
        key: 'plans.read',
        label: 'Read plans',
        description: 'View the plan catalog available to this account.',
        scope: 'TENANT',
        featureKey: 'account.overview',
    },
    'baskets.read': {
        key: 'baskets.read',
        label: 'Read baskets',
        description: 'List SIM baskets.',
        scope: 'TENANT',
        featureKey: 'inventory.baskets',
    },
    'sims.read': {
        key: 'sims.read',
        label: 'Read SIMs',
        description: 'List the SIMs inside a basket and view SIM detail.',
        scope: 'TENANT',
        featureKey: 'inventory.baskets',
    },
    'sims.export': {
        key: 'sims.export',
        label: 'Export inventory',
        description: 'Request an inventory export, list export history and download export files.',
        scope: 'TENANT',
        featureKey: 'inventory.export',
    },
    'sims.activate': {
        key: 'sims.activate',
        label: 'Activate SIM',
        description: 'Activate a SIM with a plan, or activate a SIM out of test mode.',
        scope: 'TENANT',
        featureKey: 'sims.lifecycle.activate',
    },
    'sims.activate_test': {
        key: 'sims.activate_test',
        label: 'Activate SIM in test mode',
        description: 'Activate a SIM in test mode.',
        scope: 'TENANT',
        featureKey: 'sims.lifecycle.test_mode',
    },
    'sims.swap': {
        key: 'sims.swap',
        label: 'Swap SIM',
        description: 'Swap an existing SIM for a new one.',
        scope: 'TENANT',
        featureKey: 'sims.lifecycle.swap',
    },
    'sims.suspend': {
        key: 'sims.suspend',
        label: 'Place SIM in safe custody',
        description: 'Voluntarily suspend a SIM (safe custody).',
        scope: 'TENANT',
        featureKey: 'sims.lifecycle.safe_custody',
    },
    'sims.resume': {
        key: 'sims.resume',
        label: 'Resume SIM from safe custody',
        description: 'Take a SIM out of safe custody.',
        scope: 'TENANT',
        featureKey: 'sims.lifecycle.safe_custody',
    },
    'sims.disconnect': {
        key: 'sims.disconnect',
        label: 'Temporarily disconnect SIM',
        description: 'Temporarily disconnect a SIM and resume it from temporary disconnection.',
        scope: 'TENANT',
        featureKey: 'sims.lifecycle.disconnect',
    },
    'sims.change_plan': {
        key: 'sims.change_plan',
        label: 'Change SIM plan',
        description: 'List upgradable SIMs and change a SIM plan.',
        scope: 'TENANT',
        featureKey: 'sims.plan_upgrade',
    },
    'sims.usage.read': {
        key: 'sims.usage.read',
        label: 'Read data usage',
        description: 'View real-time data usage for a SIM.',
        scope: 'TENANT',
        featureKey: 'sims.usage',
    },
    'kyc.bulk.write': {
        key: 'kyc.bulk.write',
        label: 'Upload bulk KYC',
        description: 'Upload a bulk KYC file and download the KYC template.',
        scope: 'TENANT',
        featureKey: 'kyc.bulk',
    },
    'jobs.read': {
        key: 'jobs.read',
        label: 'Read jobs',
        description: 'View the job list, job detail and per-line-item job orders.',
        scope: 'TENANT',
        featureKey: 'jobs.tracking',
    },
    'sms.quota.read': {
        key: 'sms.quota.read',
        label: 'Read SMS quota',
        description: 'View the remaining SMS quota.',
        scope: 'TENANT',
        featureKey: 'messaging.centre',
    },
    'sms.billing.read': {
        key: 'sms.billing.read',
        label: 'Read SMS billing history',
        description: 'View SMS pack purchase and billing history.',
        scope: 'TENANT',
        featureKey: 'messaging.centre',
    },
    'sms.templates.read': {
        key: 'sms.templates.read',
        label: 'Read SMS templates',
        description: 'List SMS templates.',
        scope: 'TENANT',
        featureKey: 'messaging.centre',
    },
    'sms.templates.write': {
        key: 'sms.templates.write',
        label: 'Write SMS templates',
        description: 'Create and update SMS templates.',
        scope: 'TENANT',
        featureKey: 'messaging.centre',
    },
    'sms.send': {
        key: 'sms.send',
        label: 'Send SMS',
        description: 'Send templated and custom SMS messages.',
        scope: 'TENANT',
        featureKey: 'messaging.centre',
    },
    'sms.send_bulk': {
        key: 'sms.send_bulk',
        label: 'Send bulk SMS',
        description: 'Download the batch template and send batch SMS from a file.',
        scope: 'TENANT',
        featureKey: 'messaging.centre',
    },
    'sms.history.read': {
        key: 'sms.history.read',
        label: 'Read SMS history',
        description: 'View the SMS box, SMS jobs and SMS job orders.',
        scope: 'TENANT',
        featureKey: 'messaging.centre',
    },
    'whitelist.sms.read': {
        key: 'whitelist.sms.read',
        label: 'Read SMS whitelist',
        description: 'Fetch whitelisted SMS numbers.',
        scope: 'TENANT',
        featureKey: 'whitelist.sms',
    },
    'whitelist.sms.write': {
        key: 'whitelist.sms.write',
        label: 'Write SMS whitelist',
        description: 'Batch update whitelisted SMS numbers.',
        scope: 'TENANT',
        featureKey: 'whitelist.sms',
    },
    'whitelist.voice.read': {
        key: 'whitelist.voice.read',
        label: 'Read voice whitelist',
        description: 'Fetch whitelisted voice numbers.',
        scope: 'TENANT',
        featureKey: 'whitelist.voice',
    },
    'whitelist.voice.write': {
        key: 'whitelist.voice.write',
        label: 'Write voice whitelist',
        description: 'Update whitelisted voice numbers.',
        scope: 'TENANT',
        featureKey: 'whitelist.voice',
    },
    'whitelist.country.read': {
        key: 'whitelist.country.read',
        label: 'Read country whitelist',
        description: 'View the country catalog, whitelisted countries and whitelist requests.',
        scope: 'TENANT',
        featureKey: 'whitelist.country',
    },
    'whitelist.country.write': {
        key: 'whitelist.country.write',
        label: 'Write country whitelist',
        description: 'Request whitelisting of countries.',
        scope: 'TENANT',
        featureKey: 'whitelist.country',
    },
    'location.read': {
        key: 'location.read',
        label: 'Read location',
        description: 'Fetch the address associated with an MSISDN.',
        scope: 'TENANT',
        featureKey: 'location.centre',
    },
    'location.request': {
        key: 'location.request',
        label: 'Request location whitelisting',
        description: 'Initiate location whitelisting for a set of numbers.',
        scope: 'TENANT',
        featureKey: 'location.centre',
    },
    'diagnostics.read': {
        key: 'diagnostics.read',
        label: 'Read diagnostics',
        description: 'Run AAA/RADIUS device diagnostics.',
        scope: 'TENANT',
        featureKey: 'diagnostics.radius',
    },
    'arp.read': {
        key: 'arp.read',
        label: 'Read ARP',
        description: 'View ARP SIMs, next plans and the default expiration action.',
        scope: 'TENANT',
        featureKey: 'arp.management',
    },
    'arp.write': {
        key: 'arp.write',
        label: 'Write ARP',
        description: 'Update per-SIM expiration actions and the account default action.',
        scope: 'TENANT',
        featureKey: 'arp.management',
    },
    'arp.bulk.write': {
        key: 'arp.bulk.write',
        label: 'ARP bulk operations',
        description: 'Upload ARP bulk files and request ARP bulk exports.',
        scope: 'TENANT',
        featureKey: 'arp.bulk',
    },
    'users.read': {
        key: 'users.read',
        label: 'Read users',
        description: 'List users in own tenant.',
        scope: 'TENANT',
        featureKey: 'platform.user_management',
    },
    'users.write': {
        key: 'users.write',
        label: 'Write users',
        description: 'Invite, edit and disable users in own tenant, and manage custom roles.',
        scope: 'TENANT',
        featureKey: 'platform.user_management',
    },
    'roles.read': {
        key: 'roles.read',
        label: 'Read roles',
        description: 'List roles and their permission keys.',
        scope: 'TENANT',
        featureKey: 'platform.user_management',
    },
    'audit.read': {
        key: 'audit.read',
        label: 'Read audit log',
        description: "View own tenant's audit log.",
        scope: 'TENANT',
        featureKey: 'platform.audit_log',
    },
    'branding.read': {
        key: 'branding.read',
        label: 'Read branding',
        description: 'Read own branding configuration.',
        scope: 'TENANT',
        featureKey: null,
    },
    'branding.write': {
        key: 'branding.write',
        label: 'Write branding',
        description: 'Change own logo, colours and product name.',
        scope: 'TENANT',
        featureKey: null,
    },
    // Appended in Phase 08 (keys 41–42) rather than slotted next to their
    // subject-matter neighbours: `sortOrder` is positional, and inserting
    // mid-list would renumber every key after it in §4.1/§4.2 for no gain.
    'sims.identifiers.reveal': {
        key: 'sims.identifiers.reveal',
        label: 'Reveal full identifiers',
        description: 'See unmasked MSISDN/ICCID/IMSI/EID and download exports containing them. Every use is audited.',
        scope: 'TENANT',
        // No featureKey: like branding.*, this is not a purchasable capability —
        // it modulates how data the tenant already reads is presented.
        featureKey: null,
    },
    'webhooks.manage': {
        key: 'webhooks.manage',
        label: 'Manage webhooks',
        description: 'Create, update, test and delete webhook subscriptions and view delivery history.',
        scope: 'TENANT',
        featureKey: 'platform.webhooks',
    },
    // ── Platform scope (11) — ARCHITECTURE §4.2 ──────────────────────────────
    'admin.tenants.read': {
        key: 'admin.tenants.read',
        label: 'Read tenants',
        description: 'List and read any tenant.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.tenants.write': {
        key: 'admin.tenants.write',
        label: 'Write tenants',
        description: 'Create, rename, suspend and archive a tenant.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.features.read': {
        key: 'admin.features.read',
        label: 'Read entitlements',
        description: "Read any tenant's entitlement matrix, including NOT_ENTITLED rows.",
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.features.write': {
        key: 'admin.features.write',
        label: 'Write entitlements',
        description: "Change any tenant's entitlements.",
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.credentials.read': {
        key: 'admin.credentials.read',
        label: 'Read credential metadata',
        description: 'See vendor credential metadata and health only — never plaintext.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.credentials.write': {
        key: 'admin.credentials.write',
        label: 'Write credentials',
        description: "Set, rotate and revoke a tenant's vendor credentials.",
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.users.read': {
        key: 'admin.users.read',
        label: 'Read users across tenants',
        description: 'List users across all tenants.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.users.write': {
        key: 'admin.users.write',
        label: 'Write users across tenants',
        description: 'Create and disable users in any tenant and force a password reset.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.branding.write': {
        key: 'admin.branding.write',
        label: 'Write branding for any tenant',
        description: 'Set branding for any tenant.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.audit.read': {
        key: 'admin.audit.read',
        label: 'Read cross-tenant audit log',
        description: 'View the audit log across all tenants.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    'admin.vendor.diagnostics': {
        key: 'admin.vendor.diagnostics',
        label: 'Vendor diagnostics',
        description: 'View upstream call telemetry, token health and run connectivity probes.',
        scope: 'PLATFORM',
        featureKey: null,
    },
    // ── Reserved (4) — ARCHITECTURE §4.3 ─────────────────────────────────────
    // Belong to the three vendor groups that answer 403 on this account. Seeded
    // so the superadmin console can render them as greyed rows; attached to no
    // default role and guarding no route (their vendor paths are not in the spec
    // and must not be invented).
    'orders.bulk.write': {
        key: 'orders.bulk.write',
        label: 'Bulk orders (reserved)',
        description: 'Reserved. Generic bulk orders — upstream account is not entitled.',
        scope: 'TENANT',
        featureKey: 'orders.bulk_generic',
    },
    'whitelist.data.read': {
        key: 'whitelist.data.read',
        label: 'Read data whitelist (reserved)',
        description: 'Reserved. Data whitelisting — upstream account is not entitled.',
        scope: 'TENANT',
        featureKey: 'whitelist.data',
    },
    'whitelist.data.write': {
        key: 'whitelist.data.write',
        label: 'Write data whitelist (reserved)',
        description: 'Reserved. Data whitelisting — upstream account is not entitled.',
        scope: 'TENANT',
        featureKey: 'whitelist.data',
    },
    'kyc.details.read': {
        key: 'kyc.details.read',
        label: 'Read KYC details (reserved)',
        description: 'Reserved. KYC details — upstream account is not entitled.',
        scope: 'TENANT',
        featureKey: 'kyc.details',
    },
};
/** All 57 keys in declaration order. Index doubles as `Permission.sortOrder`. */
export const PERMISSION_KEYS = Object.keys(PERMISSIONS);
/** The 4 reserved keys (§4.3) — never attached to a default role. */
export const RESERVED_PERMISSION_KEYS = [
    'orders.bulk.write',
    'whitelist.data.read',
    'whitelist.data.write',
    'kyc.details.read',
];
/** The 11 platform-scope keys (§4.2). */
export const PLATFORM_PERMISSION_KEYS = PERMISSION_KEYS.filter((k) => PERMISSIONS[k].scope === 'PLATFORM');
/** The 42 *active* tenant-scope keys (§4.1) — reserved keys excluded. */
export const TENANT_PERMISSION_KEYS = PERMISSION_KEYS.filter((k) => PERMISSIONS[k].scope === 'TENANT' &&
    !RESERVED_PERMISSION_KEYS.includes(k));
export function isReservedPermissionKey(value) {
    return RESERVED_PERMISSION_KEYS.includes(value);
}
export function isPermissionKey(value) {
    return Object.prototype.hasOwnProperty.call(PERMISSIONS, value);
}
//# sourceMappingURL=permissions.js.map